import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getIssuedChequeById } from 'src/redux/slices/issued-cheque';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LoadingScreen } from 'src/components/loading-screen';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { IssuedChequeStatus } from 'src/types/issued-cheque';

import IssuedChequeNewEditForm from '../issued-cheque-new-edit-form';

type Props = {
  id: string;
};

export default function IssuedChequeEditView({ id }: Props) {
  const { item, itemStatus, itemError } = useAppSelector((state) => state.issuedCheque);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getIssuedChequeById({ id }));
  }, [dispatch, id]);

  if (itemStatus === 'loading' || itemStatus === 'idle') {
    return <LoadingScreen />;
  }

  // Details are only editable while nothing has happened to the cheque.
  if (itemStatus === 'failure' || !item || item.status !== IssuedChequeStatus.ISSUED) {
    return (
      <ErrorContent
        filled
        title={
          itemStatus === 'failure' ? 'Failed to load cheque' : 'This cheque can no longer be edited'
        }
        description={
          itemStatus === 'failure' ? itemError : 'Only a cheque that is still Issued can be edited.'
        }
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.issuedCheque.root}
            variant="contained"
          >
            Back to Issued Cheques
          </Button>
        }
        sx={{ py: 10 }}
      />
    );
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Edit issued cheque"
        links={[
          { name: 'Issued Cheques', href: paths.dashboard.issuedCheque.root },
          { name: item.chequeNumber, href: paths.dashboard.issuedCheque.view(item._id) },
          { name: 'Edit' },
        ]}
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <IssuedChequeNewEditForm currentCheque={item} />
    </Container>
  );
}
